import { Dispatch, MouseEvent, SetStateAction, useState } from 'react';

import { Delete, Sort } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import { ConfirmDialog } from 'src/components/ConfimDialog';
import { TableColumnsPreference } from 'src/types/wallet-monitor';

import {
  deleteTableColumnsPreferences,
  getTableColumnsPreferences,
  saveWalletTableColumnsPreferences,
} from '../../../../../services/wallet-monitor';

type TablePreference = {
  id: number;
  title: string;
  data: any;
};

export default function MyTablePreferencesButton({
  walletId,
  handleSelectPreference,
  disabled,
  selectedTableColumnsPreference,
  setSelectedTableColumnsPreference,
}: {
  walletId: number;
  handleSelectPreference: (preference?: TablePreference) => void;
  disabled: boolean;
  selectedTableColumnsPreference?: TableColumnsPreference | null;
  setSelectedTableColumnsPreference: Dispatch<
    SetStateAction<TableColumnsPreference | null>
  >;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [preferenceToDelete, setPreferenceToDelete] =
    useState<TablePreference | null>(null);

  const {
    isLoading: isLoadingWalletPreferences,
    isFetching: isFetchingWalletPreferences,
    data: walletPreferences,
    refetch: refetchWalletPreferences,
  } = useQuery({
    queryKey: ['table-preferences-list'],
    queryFn: () => getTableColumnsPreferences({}),
    enabled: !!walletId,
    cacheTime: 0,
  });

  const deletePreferenceMutation = useMutation(deleteTableColumnsPreferences, {
    onSuccess: () => {
      setOpenConfirmDeleteDialog(false);
      refetchWalletPreferences();
    },
  });

  const applyPreferenceMutation = useMutation(
    saveWalletTableColumnsPreferences,
    {
      onSuccess: (data) => {
        setAnchorEl(null);
        if (data?.preference) {
          setSelectedTableColumnsPreference(data.preference);
        }
      },
    },
  );

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    refetchWalletPreferences();
  };

  const handleDelete = () => {
    if (preferenceToDelete?.id) {
      deletePreferenceMutation.mutate(preferenceToDelete.id);
      // Se a preferência deletada é a selecionada, limpa a seleção
      if (selectedTableColumnsPreference?.id === preferenceToDelete.id) {
        setSelectedTableColumnsPreference(null);
        handleSelectPreference(undefined);
      }
    }
  };

  const handleItemClick = (preference?: TablePreference) => {
    if (preference) {
      handleSelectPreference(preference);
      applyPreferenceMutation.mutate({
        walletId,
        preferenceId: preference.id,
      });
    } else {
      handleSelectPreference(undefined);
      setSelectedTableColumnsPreference(null);
    }
  };

  return (
    <>
      <IconButton
        aria-label="minhas-preferencias"
        onClick={handleOpen}
        disabled={disabled}
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&::after': {
            content: selectedTableColumnsPreference
              ? `"${selectedTableColumnsPreference.title}"`
              : '"Minhas preferências"',
            color: disabled ? 'inherit' : '#4F4F4F',
            fontSize: 14,
            fontWeight: 600,
            marginLeft: 1,
          },
        }}>
        <Sort
          color={disabled ? 'disabled' : 'primary'}
          sx={{ transform: 'scaleY(-1)' }}
        />
      </IconButton>
      {!!anchorEl && (
        <>
          <Menu
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            open={!!anchorEl}
            MenuListProps={{
              sx: {
                minWidth: '300px',
                maxWidth: '300px',
                maxHeight: '200px',
              },
            }}
            PaperProps={{
              sx: (theme) => ({
                '&::-webkit-scrollbar': {
                  height: 10,
                  width: 10,
                  WebkitAppearance: 'none',
                  border: '1px solid #E0E0E0',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: 5,
                  backgroundColor: theme.palette.grey['800'],
                },
              }),
            }}>
            {isLoadingWalletPreferences || isFetchingWalletPreferences ? (
              <MenuItem sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={20} />
              </MenuItem>
            ) : walletPreferences?.length > 0 ? (
              [
                <MenuItem
                  key="none-preference"
                  onClick={() => handleItemClick(undefined)}
                  title="Nenhuma">
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    width="100%"
                    alignItems="center">
                    <Typography
                      sx={{
                        height: 'fit-content',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}>
                      Nenhuma
                    </Typography>
                  </Box>
                </MenuItem>,
                ...walletPreferences.map(
                  (preference: TableColumnsPreference) => (
                    <MenuItem
                      key={`${preference.id}-preference`}
                      onClick={() => handleItemClick(preference)}
                      title={preference.title}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        width="100%"
                        alignItems="center">
                        <Typography
                          sx={{
                            height: 'fit-content',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '14px',
                            fontWeight: 500,
                          }}>
                          {preference.title}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            setPreferenceToDelete(preference);
                            setOpenConfirmDeleteDialog(true);
                          }}
                          disabled={deletePreferenceMutation.isLoading}>
                          <Delete sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </Box>
                    </MenuItem>
                  ),
                ),
              ]
            ) : (
              <MenuItem
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
                disableRipple>
                Sem preferências salvas.
              </MenuItem>
            )}
          </Menu>
          <ConfirmDialog
            open={openConfirmDeleteDialog}
            onClose={() => setOpenConfirmDeleteDialog(false)}
            submitAction={handleDelete}
            title={`Tem certeza que deseja remover a preferência "${preferenceToDelete?.title}"?`}
            hasActionButton={true}
          />
        </>
      )}
    </>
  );
}
